import { type Position, type EndGameScore, Animal, GameResult } from "./types"
import { Grid } from "./grid"
import { findIslands } from "./island"
import { resolvePredation } from "./predation"
import {
  SCORE_PLACEMENT, SCORE_SHEEP_ALIVE, SCORE_DEER_ALIVE,
  SCORE_WOLF_ALIVE, SCORE_BEAR_ALIVE, SCORE_PERFECT_ISLAND,
  PENALTY_HERBIVORE_DEATH, PERFECT_ISLAND_MIN_TILES,
  VICTORY_THRESHOLD, MINOR_VICTORY_THRESHOLD,
} from "./constants"

/** Score for placing a tile: +10 × combo_current */
export function placementScore(comboCurrent: number): number {
  return SCORE_PLACEMENT * comboCurrent
}

/** Update combo after a turn. Returns new combo_current. */
export function updateCombo(comboCurrent: number, herbivoreDeaths: number, comboMax: number): number {
  if (herbivoreDeaths > 0) return 1
  return Math.min(comboCurrent + 1, comboMax)
}

/** Penalty for herbivore deaths */
export function deathPenalty(herbivoreDeaths: number): number {
  if (herbivoreDeaths === 0) return 0
  return PENALTY_HERBIVORE_DEATH * herbivoreDeaths
}

/** Check if an island is "perfect": >= 6 tiles, no predator/prey conflicts possible */
export function isPerfectIsland(grid: Grid, island: Position[]): boolean {
  if (island.length < PERFECT_ISLAND_MIN_TILES) return false
  // Run predation in dryRun mode to check for potential conflicts
  const result = resolvePredation(grid, island, true)
  return result.kills.length === 0
}

const ANIMAL_SCORES: Record<Animal, number> = {
  [Animal.Sheep]: SCORE_SHEEP_ALIVE,
  [Animal.Deer]: SCORE_DEER_ALIVE,
  [Animal.Wolf]: SCORE_WOLF_ALIVE,
  [Animal.Bear]: SCORE_BEAR_ALIVE,
}

/** Calculate end-of-game score */
export function endGameScore(
  grid: Grid,
  placementTotal: number,
  penaltyTotal: number,
  comboBest: number,
  initialSheepCount: number,
): EndGameScore {
  // Count surviving animals
  let animalScore = 0
  let survivingSheep = 0

  grid.forEachTile((_pos, tile) => {
    if (tile.animal) {
      animalScore += (ANIMAL_SCORES[tile.animal] || 0) * comboBest
      if (tile.animal === Animal.Sheep) survivingSheep++
    }
  })

  // Perfect islands
  const islands = findIslands(grid)
  let perfectIslandCount = 0
  for (const island of islands) {
    if (isPerfectIsland(grid, island)) perfectIslandCount++
  }
  const perfectIslandScore = perfectIslandCount * SCORE_PERFECT_ISLAND * comboBest

  const finalScore = placementTotal + animalScore + perfectIslandScore + penaltyTotal

  // Victory condition based on sheep survival
  const sheepSurvivalRate = initialSheepCount > 0 ? survivingSheep / initialSheepCount : 1

  let result: GameResult
  if (sheepSurvivalRate >= VICTORY_THRESHOLD) {
    result = GameResult.Victory
  } else if (sheepSurvivalRate >= MINOR_VICTORY_THRESHOLD) {
    result = GameResult.MinorVictory
  } else {
    result = GameResult.Defeat
  }

  return {
    placementTotal,
    animalScore,
    perfectIslandScore,
    penaltyTotal,
    finalScore,
    sheepSurvivalRate,
    result,
  }
}
